/* eslint complexity: ["error", 40] */

import React, {Fragment, useEffect, useMemo, useRef, useState} from 'react';
import PropTypes from 'prop-types';
import {connect} from 'react-redux';
import {withRouter, Redirect} from 'react-router-dom';
import {Button, Carousel, Container, Fade, Form, Modal, Spinner} from 'react-bootstrap';
import CommonMetaTag from './common-meta-tag';
import {push} from 'connected-react-router';
import {
  partnerSelected,
  getPartnerFields,
  getPartnerServices,
  getPartners,
  filterBookingFieldId,
  getSlots,
  getBookings,
  addPartnerToFavourite,
  removePartnerFromFavourite,
  filterBookableFields,
  getPartnerEvents,
  showPlayerChooseRtvChannelModal,
  rtvChannelSelected
} from '../actions/player';
import {SPORTS} from '../constants';
import {useTranslation} from 'react-i18next';
import {FaCalendarAlt, FaCalendarPlus, FaClock, FaFutbol, FaHome, FaInfoCircle, FaMapMarkerAlt, FaSearch, FaTv} from 'react-icons/fa';
import Configuration from '../config';
import restaurant from '../images/ristorante.svg';
import bar from '../images/bar_icon.svg';
import relax from '../images/relax_icon.svg';
import showers from '../images/shower_icon.svg';
import shop from '../images/negozio.svg';
import parking from '../images/parking_icon.svg';
import petFriendly from '../images/pets_icon.svg';
import lockerRooms from '../images/locker_room_icon.svg';
import tournaments from '../images/tournaments_icon.svg';
import picnic from '../images/picnic_area_icon.svg';
import camIcon from '../images/cam_icon.svg';
import replayerTvIcon from '../images/replayer_tv_icon.svg';
import PlayerNewSubscription from './player-new-subscription';
import PlayerInsufficientFundsSubscription from './player-insufficient-funds';
import PlayerChooseRtvChannel from './player-choose-rtv-channel';
import {getFieldSlots as apiGetFieldSlots, getPartnerFields as apiGetPartnerFields} from '../api';
import {CLUB_PENDING_BOOKING_SLOT_KEY} from '../util/club-mode';

const getSportCenterActionClassName = hasFullFirstAction => (
  `sport-center-card-actions ${hasFullFirstAction ? 'sport-center-card-actions--has-full-first' : ''} mt-auto mb-3`
);

const todayInputValue = () => new Date().toISOString().slice(0, 10);
const currentHourInputValue = () => {
  const now = new Date();
  return `${String(now.getHours()).padStart(2, '0')}:00`;
};
const slotTimeValue = value => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return `${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`;
};
const minutesFromTime = time => {
  const [hours, minutes] = String(time || '').split(':').map(Number);
  if (!Number.isFinite(hours) || !Number.isFinite(minutes)) return null;
  return (hours * 60) + minutes;
};
const timeOptionsFromSlots = slots => Array.from(new Set(
  slots.map(slot => slotTimeValue(slot.startDatetime)).filter(Boolean)
)).sort();
const closestTimeOption = (options, preferredTime) => {
  if (!options.length) return preferredTime || currentHourInputValue();
  const targetMinutes = minutesFromTime(preferredTime) ?? minutesFromTime(currentHourInputValue());
  return options.reduce((closest, option) => {
    const closestDistance = Math.abs((minutesFromTime(closest) ?? 0) - targetMinutes);
    const optionDistance = Math.abs((minutesFromTime(option) ?? 0) - targetMinutes);
    return optionDistance < closestDistance ? option : closest;
  }, options[0]);
};
const formatTime = value => new Date(value).toLocaleTimeString([], {hour: '2-digit', minute: '2-digit'});
const slotId = slot => slot.slotId || slot.id;
const normalizeFilterText = value => (value || '').toString().toLowerCase().trim();
const normalizeSportValue = value => normalizeFilterText(value)
  .replace(/^sport[-_\s]*/, '')
  .replace(/^replayer[-_\s]*/, '');
const SPORT_LABELS = {
  tennis: 'tennis',
  padel: 'padel',
  pickleball: 'pickleball'
};
const sportIconFor = sport => {
  const entry = Object.entries(SPORTS).find(([name]) => normalizeSportValue(name) === normalizeSportValue(sport));
  return entry ? entry[1].icon : null;
};
const sportDisplayName = sport => {
  const normalized = normalizeSportValue(sport);
  if (SPORT_LABELS[normalized]) return SPORT_LABELS[normalized];
  return normalized || String(sport || '');
};
const filterSlotsAroundTime = (slots, date, time) => {
  if (!time) return slots;
  const targetTime = new Date(`${date}T${time}:00`).getTime();
  if (!Number.isFinite(targetTime)) return slots;
  const windowSizeMs = 60 * 60 * 1000;
  return [...slots]
    .filter(slot => {
      const slotTime = new Date(slot.startDatetime).getTime();
      return Number.isFinite(slotTime) && slotTime >= targetTime - windowSizeMs && slotTime <= targetTime + windowSizeMs;
    })
    .sort((firstSlot, secondSlot) => new Date(firstSlot.startDatetime) - new Date(secondSlot.startDatetime));
};
const isBookableSlot = slot => (
  slot &&
  new Date(slot.startDatetime) > new Date() &&
  slot.state === 'available' &&
  Number(slot.availablePlaces || 0) > 0
);

export const readQuickBookingFilters = search => {
  const params = new URLSearchParams(search || '');
  return {
    partnerId: params.get('quickPartnerId') || '',
    sport: params.get('sport') || '',
    date: params.get('date') || todayInputValue(),
    time: params.get('time') || currentHourInputValue()
  };
};

export const writeQuickBookingFilters = (search, filters) => {
  const params = new URLSearchParams(search || '');
  const values = {
    quickPartnerId: filters.partnerId,
    sport: filters.sport,
    date: filters.date,
    time: filters.time
  };
  Object.entries(values).forEach(([name, value]) => {
    if (value) params.set(name, String(value));
    else params.delete(name);
  });
  const nextSearch = params.toString();
  return nextSearch ? `?${nextSearch}` : '';
};

export const persistQuickBookingFilters = (history, filters) => {
  history.replace({
    pathname: history.location.pathname,
    search: writeQuickBookingFilters(history.location.search, filters)
  });
};

export const BookingSearchModeSelector = ({labels, onOpenPlanner, plannerDisabled}) => (
  <div className="sport-centers-booking-mode" role="group" aria-label={labels.group}>
    <button
      type="button"
      className="sport-centers-booking-mode-option is-active"
      aria-pressed="true"
    >
      <FaSearch aria-hidden="true"/>
      <span>{labels.filters}</span>
    </button>
    <button
      type="button"
      className="sport-centers-booking-mode-option"
      aria-pressed="false"
      disabled={plannerDisabled}
      onClick={onOpenPlanner}
    >
      <FaCalendarAlt aria-hidden="true"/>
      <span>{labels.planner}</span>
    </button>
  </div>
);

BookingSearchModeSelector.propTypes = {
  labels: PropTypes.shape({
    filters: PropTypes.string.isRequired,
    group: PropTypes.string.isRequired,
    planner: PropTypes.string.isRequired
  }).isRequired,
  onOpenPlanner: PropTypes.func,
  plannerDisabled: PropTypes.bool
};

export const openQuickBookingPlanner = ({showCalendar, partner, dateInput, fallbackDate, fieldId = null, resumeSlotId = null}) => {
  if (!partner || typeof showCalendar !== 'function') return;
  const targetDate = dateInput && dateInput.value ? dateInput.value : fallbackDate;
  const openCalendar = showCalendar(partner, fieldId, true, targetDate, resumeSlotId);
  if (typeof openCalendar === 'function') openCalendar();
};

const PlayerShowPartners = ({
  token,
  partners,
  showPartner,
  showCalendar,
  toggleFavourite,
  favouritePartners,
  chooseReplayerTV,
  bookings,
  loadBookings,
  loadPartners,
  history
}) => {
  const {t} = useTranslation();
  const [initialQuickFilters] = useState(() => readQuickBookingFilters(history.location.search));

  const [partnerFilter, setPartnerFilter] = useState("");
  const [sportFilter, setSportFilter] = useState(initialQuickFilters.sport);
  const [quickPartnerId, setQuickPartnerId] = useState(initialQuickFilters.partnerId);
  const [quickDate, setQuickDate] = useState(initialQuickFilters.date);
  const [quickTime, setQuickTime] = useState(initialQuickFilters.time);
  const [quickFields, setQuickFields] = useState([]);
  const [quickAvailableSlots, setQuickAvailableSlots] = useState([]);
  const [quickSlots, setQuickSlots] = useState([]);
  const [quickSlotsLoading, setQuickSlotsLoading] = useState(false);
  const [quickSlotsError, setQuickSlotsError] = useState(false);
  const [clubPagePartner, setClubPagePartner] = useState(null);
  const quickDateInputRef = useRef(null);
  const bookingSearchParams = new URLSearchParams(history.location.search);
  const showOnlyBookablePartners = bookingSearchParams.get('bookable') === 'true';
  const lockedQuickPartnerId = showOnlyBookablePartners ? bookingSearchParams.get('partnerId') : null;

  useEffect(() => {
    if (token !== null && showOnlyBookablePartners) loadBookings();
  }, [loadBookings, showOnlyBookablePartners, token]);

  useEffect(() => {
    if (token !== null && partners.length === 0) loadPartners();
  }, [loadPartners, partners.length, token]);

  const isFavourite = uid => favouritePartners.some(p => p.uid === uid);
  const getPartnerPublicSlug = partner => partner.publicSlug || partner.public_slug || null;
  const partnerMatchesFilter = partner => {
    const normalizedFilter = normalizeFilterText(partnerFilter).trim();
    if (normalizedFilter === '') {
      return true;
    }
    return normalizeFilterText(partner.name).indexOf(normalizedFilter) > -1;
  };
  const partnerMatchesSport = partner => {
    const normalizedSport = normalizeFilterText(sportFilter).trim();
    if (normalizedSport === '') {
      return true;
    }
    const sportsSource = showOnlyBookablePartners && Array.isArray(partner.bookableSports)
      ? partner.bookableSports
      : partner.sports || [];
    return sportsSource.some(sport => normalizeSportValue(sport) === normalizedSport);
  };

  const eligiblePartners = partners.filter(p =>
    !p.isSpecialist &&
    (!showOnlyBookablePartners || p.bookableFields)
  );
  const sportOptions = eligiblePartners
    .reduce((sports, partner) => {
      const sportsSource = showOnlyBookablePartners && Array.isArray(partner.bookableSports)
        ? partner.bookableSports
        : partner.sports || [];
      return sports.concat(sportsSource);
    }, [])
    .map(sport => sport.toString().trim())
    .filter((sport, index, sports) => sport !== '' && sports.findIndex(item => normalizeFilterText(item) === normalizeFilterText(sport)) === index)
    .sort((firstSport, secondSport) => firstSport.localeCompare(secondSport));

  const lastBookingByPartner = useMemo(() => (bookings || []).reduce((lastBookings, booking) => {
    if (!booking || booking.partnerId === null || booking.partnerId === undefined || !booking.startDatetime) return lastBookings;
    const timestamp = new Date(booking.startDatetime).getTime();
    if (!Number.isFinite(timestamp)) return lastBookings;
    const partnerId = String(booking.partnerId);
    if (!lastBookings[partnerId] || timestamp > lastBookings[partnerId]) {
      return {...lastBookings, [partnerId]: timestamp};
    }
    return lastBookings;
  }, {}), [bookings]);

  const sortBookablePartners = (firstPartner, secondPartner) => {
    if (!showOnlyBookablePartners) return 0;
    const firstLastBooking = lastBookingByPartner[String(firstPartner.uid)] || 0;
    const secondLastBooking = lastBookingByPartner[String(secondPartner.uid)] || 0;
    if (firstLastBooking !== secondLastBooking) return secondLastBooking - firstLastBooking;
    const firstFavourite = isFavourite(firstPartner.uid);
    const secondFavourite = isFavourite(secondPartner.uid);
    if (firstFavourite !== secondFavourite) return firstFavourite ? -1 : 1;
    return String(firstPartner.name || firstPartner.businessName || '').localeCompare(String(secondPartner.name || secondPartner.businessName || ''));
  };

  const orderedQuickPartnerOptions = showOnlyBookablePartners ? [...eligiblePartners].sort(sortBookablePartners) : [];
  const quickPartnerOptions = lockedQuickPartnerId
    ? orderedQuickPartnerOptions.filter(partner => String(partner.uid) === String(lockedQuickPartnerId))
    : orderedQuickPartnerOptions;
  const visiblePartners = eligiblePartners.filter(p =>
    partnerMatchesFilter(p) &&
    partnerMatchesSport(p)
  ).sort(sortBookablePartners);
  const selectedQuickPartner = quickPartnerOptions.find(partner => String(partner.uid) === String(quickPartnerId)) || quickPartnerOptions[0] || null;
  const selectedQuickPartnerSports = selectedQuickPartner
    ? (Array.isArray(selectedQuickPartner.bookableSports) && selectedQuickPartner.bookableSports.length > 0 ? selectedQuickPartner.bookableSports : selectedQuickPartner.sports || [])
    : [];
  const activeSportOptions = showOnlyBookablePartners && selectedQuickPartner ? selectedQuickPartnerSports : sportOptions;
  const selectedPartnerCards = selectedQuickPartner ? [selectedQuickPartner] : visiblePartners;

  useEffect(() => {
    if (!showOnlyBookablePartners || quickPartnerOptions.length === 0) return;
    if (!quickPartnerOptions.some(partner => String(partner.uid) === String(quickPartnerId))) {
      setQuickPartnerId(String(quickPartnerOptions[0].uid));
    }
  }, [quickPartnerId, quickPartnerOptions, showOnlyBookablePartners]);

  useEffect(() => {
    if (!showOnlyBookablePartners || selectedQuickPartnerSports.length === 0) return;
    if (!selectedQuickPartnerSports.some(sport => normalizeFilterText(sport).trim() === normalizeFilterText(sportFilter).trim())) {
      setSportFilter(selectedQuickPartnerSports[0]);
    }
  }, [selectedQuickPartnerSports, showOnlyBookablePartners, sportFilter]);

  useEffect(() => {
    const loadQuickSlots = async () => {
      if (!showOnlyBookablePartners || !selectedQuickPartner || !sportFilter || !quickDate || !token) {
        setQuickFields([]);
        setQuickAvailableSlots([]);
        setQuickSlots([]);
        return;
      }

      setQuickSlotsLoading(true);
      setQuickSlotsError(false);
      const fieldsRes = await apiGetPartnerFields(selectedQuickPartner.uid, token);
      if (fieldsRes.status !== 200 || !Array.isArray(fieldsRes.json)) {
        setQuickFields([]);
        setQuickAvailableSlots([]);
        setQuickSlots([]);
        setQuickSlotsError(true);
        setQuickSlotsLoading(false);
        return;
      }

      const normalizedSport = normalizeSportValue(sportFilter);
      const nextFields = fieldsRes.json.filter(field =>
        field.bookable !== false &&
        field.visible !== false &&
        normalizeSportValue(field.sport) === normalizedSport
      );
      const results = await Promise.all(nextFields.map(field =>
        apiGetFieldSlots(selectedQuickPartner.uid, token, field.id, false, new Date(`${quickDate}T12:00:00`), 'DAY')
      ));
      const nextSlots = [];
      results.forEach((res, index) => {
        if (res.status === 200 && Array.isArray(res.json)) {
          res.json.forEach(slot => nextSlots.push({...slot, fieldId: slot.fieldId || nextFields[index].id}));
        }
      });
      nextSlots.sort((a, b) => new Date(a.startDatetime) - new Date(b.startDatetime));
      const bookableSlots = nextSlots.filter(isBookableSlot);
      setQuickFields(nextFields);
      setQuickAvailableSlots(bookableSlots);
      setQuickSlots(filterSlotsAroundTime(bookableSlots, quickDate, quickTime));
      setQuickSlotsLoading(false);
    };

    loadQuickSlots();
  }, [quickDate, quickTime, selectedQuickPartner, showOnlyBookablePartners, sportFilter, token]);

  const quickTimeOptions = useMemo(() => timeOptionsFromSlots(quickAvailableSlots), [quickAvailableSlots]);

  useEffect(() => {
    if (!showOnlyBookablePartners || quickTimeOptions.length === 0) return;
    setQuickTime(currentTime => (
      quickTimeOptions.includes(currentTime) ? currentTime : closestTimeOption(quickTimeOptions, currentTime)
    ));
  }, [quickTimeOptions, showOnlyBookablePartners]);

  useEffect(() => {
    if (showOnlyBookablePartners) return;
    if (!sportOptions.length) return;
    if (normalizeFilterText(sportFilter).trim() === '') return;
    if (!sportOptions.some(option => normalizeFilterText(option).trim() === normalizeFilterText(sportFilter).trim())) {
      setSportFilter('');
    }
  }, [showOnlyBookablePartners, sportFilter, sportOptions]);

  const quickFieldName = fieldId => {
    const field = quickFields.find(item => Number(item.id) === Number(fieldId));
    return field ? field.name : t('FIELD');
  };
  const openPlanner = (fieldId = null, resumeSlotId = null) => () => {
    persistQuickBookingFilters(history, {
      partnerId: selectedQuickPartner ? selectedQuickPartner.uid : quickPartnerId,
      sport: sportFilter,
      date: quickDateInputRef.current && quickDateInputRef.current.value ? quickDateInputRef.current.value : quickDate,
      time: quickTime
    });
    openQuickBookingPlanner({
      showCalendar,
      partner: selectedQuickPartner,
      dateInput: quickDateInputRef.current,
      fallbackDate: quickDate,
      fieldId,
      resumeSlotId
    });
  };

  if (token === null) {
    return (
      <Redirect to="/login"/>
    );
  }

  const renderEmptyPageString = () => {
    if (visiblePartners.length === 0) {
      return <h6>{t('SEARCH_PARTNER_OR_PLAYER_RESULT_FAILED')}</h6>;
    }
    return null;
  }

  const closeClubPageModal = () => setClubPagePartner(null);
  const openPartnerShop = partner => () => {
    const slug = getPartnerPublicSlug(partner);
    if (slug) {
      window.location.href = `/shop/${slug}`;
    }
  };

  return (
    <Fragment>
      <CommonMetaTag/>
      <Fade in appear className="bg_white" style={{transition: 'opacity 0.30s linear'}}>
        <Container fluid className="presentation-container">
          <div className="page-header header-filter clear-filter dashboard_hero_rp" data-parallax="true">
            <div className="dashboard_hero_overlay_rp"/>
          </div>
          <div className="main main_raised_rp">
            <div className="section" style={{paddingBottom: '500px'}}>
              <div className="container">
                <div className="row">
                  <div className="col-md-12 ml-auto mr-auto">
                    <h4 className="section_title_neg_rp">{t('SPORT_CENTERS').toUpperCase()}</h4>
                    {
                      showOnlyBookablePartners ?
                        <div className="sport-centers-bookable-intro">
                          <div>
                            <strong>{t('BOOK_YOUR_FIELD')}</strong>
                            <span>
                              {t('BOOKABLE_SPORT_CENTERS_INTRO')}
                            </span>
                          </div>
                          <Button
                            type="button"
                            className="btn home-skywork-action home-skywork-action--primary"
                            onClick={() => history.push('/player-replayer-tv-home')}
                          >
                            <FaTv aria-hidden="true"/>
                            {t('GO_TO_REPLAYERTV')}
                          </Button>
                        </div> :
                        null
                    }
                    <div className={`sport-centers-filter-panel ${showOnlyBookablePartners ? 'sport-centers-filter-panel--quick-booking' : ''}`}>
                      {
                        showOnlyBookablePartners ?
                          <BookingSearchModeSelector
                            labels={{
                              filters: t('QUICK_BOOKING_MODE_FILTERS'),
                              group: t('QUICK_BOOKING_MODE_LABEL'),
                              planner: t('QUICK_BOOKING_MODE_PLANNER')
                            }}
                            plannerDisabled={!selectedQuickPartner || !quickDate}
                            onOpenPlanner={selectedQuickPartner ? openPlanner() : undefined}
                          /> :
                          null
                      }
                      <div className="sport-centers-filter-heading">
                        <strong>{showOnlyBookablePartners ? t('QUICK_BOOKING_WHEN_TITLE') : t('FIND_RIGHT_SPORT_CENTER')}</strong>
                        <span>{showOnlyBookablePartners ? t('QUICK_BOOKING_WHEN_HELP') : t('FIND_RIGHT_SPORT_CENTER_HELP')}</span>
                      </div>
                      <Form className="sport-centers-filter-form">
                        {
                          showOnlyBookablePartners ?
                            <Form.Group className="sport-centers-filter-field">
                              <Form.Label><FaMapMarkerAlt aria-hidden="true"/> {t('SEARCH_SPORT_CENTER')}</Form.Label>
                              <Form.Control
                                as="select"
                                name="quickPartnerId"
                                value={selectedQuickPartner ? String(selectedQuickPartner.uid) : ''}
                                disabled={Boolean(lockedQuickPartnerId)}
                                onChange={e => setQuickPartnerId(e.target.value)}
                              >
                                {
                                  quickPartnerOptions.map(partner => <option key={partner.uid} value={partner.uid}>{partner.name || partner.businessName}</option>)
                                }
                              </Form.Control>
                            </Form.Group> :
                            <Form.Group className="sport-centers-filter-field">
                              <Form.Label><FaSearch aria-hidden="true"/> {t('SEARCH_SPORT_CENTER')}</Form.Label>
                              <Form.Control name="partnerFilter" placeholder={t('SEARCH_PARTNER')} type="text" maxLength="50" value={partnerFilter} onChange={e => setPartnerFilter(e.target.value)} onKeyPress={event => {if (event.key === 'Enter') {event.preventDefault();}}}/>
                            </Form.Group>
                        }
                        <Form.Group className="sport-centers-filter-field">
                          <Form.Label><FaFutbol aria-hidden="true"/> {t('FILTER_BY_SPORT')}</Form.Label>
                          <Form.Control
                            as="select"
                            name="sportFilter"
                            className="sport-centers-filter-select"
                            value={sportFilter}
                            onChange={e => setSportFilter(e.target.value)}
                          >
                            {!showOnlyBookablePartners ? <option value="">{t('ALL_SPORTS')}</option> : null}
                            {
                              activeSportOptions.map(sport => (
                                <option key={sport} value={sport}>{sportDisplayName(sport, t)}</option>
                              ))
                            }
                          </Form.Control>
                        </Form.Group>
                        {
                          showOnlyBookablePartners ?
                            <Form.Group className="sport-centers-filter-field">
                              <Form.Label><FaCalendarAlt aria-hidden="true"/> {t('DATE')}</Form.Label>
                              <Form.Control ref={quickDateInputRef} name="quickDate" type="date" min={todayInputValue()} value={quickDate} onChange={e => setQuickDate(e.target.value)}/>
                            </Form.Group> :
                            null
                        }
                        {
                          showOnlyBookablePartners ?
                            <Form.Group className="sport-centers-filter-field">
                              <Form.Label><FaClock aria-hidden="true"/> {t('TIME')}</Form.Label>
                              <Form.Control
                                as="select"
                                name="quickTime"
                                value={quickTime}
                                disabled={quickTimeOptions.length === 0}
                                onChange={e => setQuickTime(e.target.value)}
                              >
                                {
                                  quickTimeOptions.length === 0 ?
                                    <option value={quickTime}>{quickTime || currentHourInputValue()}</option> :
                                    quickTimeOptions.map(option => <option key={option} value={option}>{option}</option>)
                                }
                              </Form.Control>
                            </Form.Group> :
                            null
                        }
                      </Form>
                      <div className="sport-centers-filter-result">{renderEmptyPageString()}</div>
                    </div>
                    {
                      showOnlyBookablePartners ?
                        <div className="sport-centers-quick-slots">
                          <div className="sport-centers-quick-slots-heading">
                            <FaSearch aria-hidden="true"/>
                            <strong>{t('QUICK_BOOKING_RESULTS_TITLE')}</strong>
                          </div>
                          {quickSlotsLoading ? <div className="sport-centers-quick-slots-state"><Spinner animation="border" size="sm"/>{t('OPEN_MATCH_CREATE_LOADING_SLOTS')}</div> : null}
                          {!quickSlotsLoading && quickSlotsError ? <div className="sport-centers-quick-slots-state">{t('OPEN_MATCH_CREATE_LOAD_FIELDS_FAILED')}</div> : null}
                          {!quickSlotsLoading && !quickSlotsError && selectedQuickPartner && quickSlots.length === 0 ? <div className="sport-centers-quick-slots-state">{t('OPEN_MATCH_CREATE_NO_SLOTS')}</div> : null}
                          {!quickSlotsLoading && quickSlots.length > 0 ? quickSlots.map(slot => {
                            const field = quickFields.find(item => Number(item.id) === Number(slot.fieldId));
                            const sport = field ? field.sport : '';
                            const sportIcon = sportIconFor(sport);
                            return (
                              <button
                                key={slotId(slot)}
                                type="button"
                                className="sport-centers-quick-slot"
                                onClick={openPlanner(slot.fieldId, slotId(slot))}
                              >
                                <FaCalendarAlt aria-hidden="true"/>
                                <span>
                                  <strong>{formatTime(slot.startDatetime)} - {formatTime(slot.endDatetime)}</strong>
                                  <small>
                                    {quickFieldName(slot.fieldId)}
                                    {sport ? (
                                      <>
                                        {' · '}
                                        <span className="sport-slot-meta">
                                          {sportIcon ? <img src={sportIcon} alt="" aria-hidden="true"/> : null}
                                          <span>{sport}</span>
                                        </span>
                                      </>
                                    ) : null}
                                  </small>
                                </span>
                                <em>{t('BOOK')}</em>
                              </button>
                            );
                          }) : null}
                        </div> :
                        null
                    }
                  </div>
                </div>
                <div className="row">
                  {
                    selectedPartnerCards.map(p =>
                      (
                        <div key={p.uid} className="col-md-6 col-lg-4 mb-sm-4 card-group">
                          <div className="card sportcenter_card_bg_rp skywork-sport-center-info-card">
                              {
                                p.imagesNames.length === 0 ?
                                  <div className="card_image_rp">
                                    <div className="videoWrapper card_image_sportcenter_rp"/>
                                  </div> :
                                  <Carousel style={{marginBottom: 10}} interval={null}>
                                    {
                                      p.imagesNames.map(i =>
                                        (
                                          <Carousel.Item key={i}>
                                            <img
                                              className="d-block w-100"
                                              src={`${Configuration.apiEndPoint}/images/${p.uid}?load=${i}`}
                                              alt={t('FIRST_SLIDE')}
                                              style={{width: '100%'}}
                                            />
                                          </Carousel.Item>
                                        )
                                      )
                                    }
                                  </Carousel>
                              }
                            <div className="card-body d-flex flex-column">
                              <div className="skywork-sport-center-name-box"><h5 className="card-title">{p.name}</h5></div>
                              {
                                isFavourite(p.uid) ?
                                  <div className="favorites_card_heart_btn_active" onClick={toggleFavourite(p.uid, true)}/> :
                                  <div className="favorites_card_heart_btn" onClick={toggleFavourite(p.uid, false)}/>
                              }
                              <ul className="list-unstyled skywork-sport-center-details-box">
                                <li className="list_location_pointer">{p.city}</li>
                                  <li><strong>{t('SPORTS')}:</strong> {p.sports.map(sport => sportDisplayName(sport, t)).join(', ')}</li>
                              </ul>
                              <div className="skywork-sport-center-description-box">{p.description || t('PLAYER_HOME_NO_DESCRIPTION_AVAILABLE')}</div>
                              <ul className="card_facilities_list_icons">
                                {
                                  p.facilities && p.facilities.parking ?
                                    <li className="card_facilities_list_icons_item"><img src={parking} alt={t('ICON_PARK')}/></li> :
                                    null
                                }
                                {
                                  p.facilities && p.facilities.lockerRooms ?
                                    <li className="card_facilities_list_icons_item"><img src={lockerRooms} alt={t('ICON_LOCKER_ROOMS')}/></li> :
                                    null
                                }
                                {
                                  p.facilities && p.facilities.showers ?
                                    <li className="card_facilities_list_icons_item"><img src={showers} alt={t('ICON_SHOWER')}/></li> :
                                    null
                                }
                                {
                                  p.facilities && p.facilities.restaurant ?
                                    <li className="card_facilities_list_icons_item"><img src={restaurant} alt={t('ICON_RESTAURANTS')}/></li> :
                                    null
                                }
                                {
                                  p.facilities && p.facilities.bar ?
                                    <li className="card_facilities_list_icons_item"><img src={bar} alt={t('ICON_BAR')}/></li> :
                                    null
                                }
                                {
                                  p.facilities && p.facilities.shop ?
                                    <li className="card_facilities_list_icons_item"><img src={shop} alt={t('ICON_SHOP')}/></li> :
                                    null
                                }
                                {
                                  p.facilities && p.facilities.picnic ?
                                    <li className="card_facilities_list_icons_item"><img src={picnic} alt={t('ICON_PIC_NIC')}/></li> :
                                    null
                                }
                                {
                                  p.facilities && p.facilities.tournaments ?
                                    <li className="card_facilities_list_icons_item"><img src={tournaments} alt={t('ICON_TOURNAMENTS')}/></li> :
                                    null
                                }
                                {
                                  p.facilities && p.facilities.petFriendly ?
                                    <li className="card_facilities_list_icons_item"><img src={petFriendly} alt={t('ICON_ANIMAL')}/></li> :
                                    null
                                }
                                {
                                  p.facilities && p.facilities.relaxArea ?
                                    <li className="card_facilities_list_icons_item"><img src={relax} alt={t('RELAX_AREA')}/></li> :
                                    null
                                }
                              </ul>
                            </div>
                            {
                              p.recording ?
                                <div className="d-flex favorites_card_badge mb-3">
                                  <img src={camIcon} className="favorites_card_badge_icon" alt={t('ICON_FAVORITES')}/>
                                  <p>{t('HD_VIDEO_SERVICE')}</p>
                                  {
                                    p.rtvChannels.length > 0 ?
                                      <Button type="button" className="btn btn_red_rp replayer-tv-card-action ml-auto" onClick={chooseReplayerTV(p)}>
                                        <span className="replayer-tv-card-action-icon">
                                          <img src={replayerTvIcon} alt=""/>
                                        </span>
                                        <span>{t('REPLAYER_TV')}</span>
                                      </Button> :
                                      null
                                  }
                                </div> :
                                null
                            }
                            <div className={getSportCenterActionClassName(getPartnerPublicSlug(p))}>
                              {
                                getPartnerPublicSlug(p) ?
                                  <Button type="button" className="btn sport-center-card-action sport-center-card-action--club" onClick={() => setClubPagePartner(p)}>
                                    <FaHome aria-hidden="true"/>
                                    {t('CLUB_PAGE_TITLE')}
                                  </Button> :
                                  null
                              }
                              {
                                p.bookableFields ?
                                  <Button type="button" className="btn sport-center-card-action sport-center-card-action--primary" onClick={showCalendar(p, null, true)}><FaCalendarPlus aria-hidden="true"/>{t('BOOK')}</Button> :
                                  null
                              }
                              {
                                p.notBookableFields ?
                                  <Button type="button" className="btn sport-center-card-action sport-center-card-action--secondary" onClick={showCalendar(p, null, false)}><FaCalendarAlt aria-hidden="true"/>{t('CALENDAR')}</Button> :
                                  null
                              }
                              {
                                p.shopActive && getPartnerPublicSlug(p) ?
                                  <Button type="button" className="btn partner-shop-card-action sport-center-card-action--shop" onClick={openPartnerShop(p)}>
                                    <span className="partner-shop-card-action-icon"><img src={shop} alt=""/></span>
                                    <span>{t('PLAYER_HOME_SHOP')}</span>
                                  </Button> :
                                  null
                              }
                              <Button type="button" className="btn sport-center-card-action sport-center-card-action--detail" onClick={showPartner(p)}><FaInfoCircle aria-hidden="true"/>{t('CENTER_DETAILS')}</Button>
                            </div>
                          </div>
                        </div>)
                    )
                  }
                </div>
              </div>
            </div>
          </div>
          <PlayerNewSubscription/>
          <PlayerInsufficientFundsSubscription/>
          <PlayerChooseRtvChannel/>
          <Modal className="modal_rp" contentClassName="bg_blue_rp" show={clubPagePartner !== null} onHide={closeClubPageModal}>
            <Modal.Header closeButton>
              <Modal.Title as="h5">{t('CLUB_PAGE_TITLE')}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
              <p>{t('CLUB_PAGE_DESCRIPTION')}</p>
            </Modal.Body>
            <Modal.Footer>
              <Button
                variant="primary"
                className="btn btn_red_rp"
                onClick={() => {
                  const publicSlug = getPartnerPublicSlug(clubPagePartner);
                  closeClubPageModal();
                  if (publicSlug) {
                    history.push(`/club/${publicSlug}`);
                  }
                }}
              >
                {t('CLUB_PAGE_OPEN')}
              </Button>
              <Button variant="secondary" className="btn btn_red_outline_rp" onClick={closeClubPageModal}>
                {t('CLOSE_BUTTON')}
              </Button>
            </Modal.Footer>
          </Modal>
        </Container>
      </Fade>
    </Fragment>
  );
};

PlayerShowPartners.propTypes = {
  token: PropTypes.string,
  partners: PropTypes.array,
  favouritePartners: PropTypes.array,
  bookings: PropTypes.array,
  showPartner: PropTypes.func.isRequired,
  showCalendar: PropTypes.func.isRequired,
  toggleFavourite: PropTypes.func.isRequired,
  chooseReplayerTV: PropTypes.func.isRequired,
  loadBookings: PropTypes.func.isRequired,
  loadPartners: PropTypes.func.isRequired,
  history: PropTypes.object.isRequired
};

PlayerShowPartners.defaultProps = {
  token: null,
  partners: [],
  favouritePartners: [],
  bookings: []
};

const mapStateToProps = ({login: {token, profile}, message, loading, player: {partners, favouritePartners, bookings}}) => ({
  token,
  profile,
  message,
  loading,
  partners,
  favouritePartners,
  bookings
});

const mapDispatchToProps = dispatch => ({
  loadBookings: () => dispatch(getBookings()),
  loadPartners: () => dispatch(getPartners()),
  showPartner: partner => () => {
    dispatch(partnerSelected(partner));
    dispatch(getPartnerFields(partner.uid));
    dispatch(getPartnerServices(partner.uid));
    dispatch(getPartnerEvents(partner.uid));
    dispatch(push('show-partner'));
  },
  showCalendar: (partner, fieldId = null, filterBookable = false, targetDate = null, resumeSlotId = null) => () => {
    const plannerDate = targetDate ? new Date(`${targetDate}T12:00:00`) : new Date();
    dispatch(partnerSelected(partner));
    dispatch(getPartnerFields(partner.uid));
    dispatch(getPartnerServices(partner.uid));
    dispatch(filterBookingFieldId(fieldId));
    dispatch(filterBookableFields(filterBookable));
    dispatch(getSlots(partner.uid, fieldId, plannerDate, 'field', 'DAY'));
    if (typeof window !== 'undefined') {
      if (resumeSlotId) window.sessionStorage.setItem(CLUB_PENDING_BOOKING_SLOT_KEY, String(resumeSlotId));
      else window.sessionStorage.removeItem(CLUB_PENDING_BOOKING_SLOT_KEY);
    }
    const params = targetDate ? `date=${encodeURIComponent(targetDate)}` : '';
    dispatch(push(filterBookable
      ? (params ? `/player-booking-calendar?${params}` : '/player-booking-calendar')
      : (params ? `/player-calendars?${params}` : '/player-calendars')));
  },
  chooseReplayerTV: (partner) => () => {
    dispatch(rtvChannelSelected(null));
    dispatch(partnerSelected(partner));
    dispatch(showPlayerChooseRtvChannelModal());
  },
  toggleFavourite: (uid, isFavourite) => () => {
    if (isFavourite) {
      dispatch(removePartnerFromFavourite(uid));
    } else {
      dispatch(addPartnerToFavourite(uid));
    }
  }
});

export default withRouter(connect(
  mapStateToProps,
  mapDispatchToProps
)(PlayerShowPartners));
